import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { KeyboardPanelOptions } from '../types/keyboard-panel.types';
import { ViewportHeightContext } from '../contexts/viewport-height-context';

const isEditable = (element: Element | null) =>
  element instanceof HTMLInputElement ||
  element instanceof HTMLTextAreaElement ||
  (element instanceof HTMLElement && element.isContentEditable);

/**
 * Exchanges an inline panel with the software keyboard without double-reserving
 * their space. Search inside the panel continues to use the visible layout.
 * @param options - Controlled panel and fallback geometry.
 * @returns Geometry and synchronous handoff actions for the consuming control.
 */
export const useKeyboardPanel = ({
  enabled,
  open,
  panelRef,
  fallbackHeight,
  fallbackViewportRatio,
  onSearchDismiss,
}: KeyboardPanelOptions) => {
  const searchKeyboardVisible = useRef(false);
  const baseline = useRef({ width: 0, height: 0 });
  const [baselineHeight, setBaselineHeight] = useState(0);
  const requestHeight = useContext(ViewportHeightContext);
  const owner = useId();
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [preferredHeight, setPreferredHeight] = useState(fallbackHeight);
  const [returning, setReturning] = useState(false);
  const [opening, setOpening] = useState(false);
  const returnStartInset = useRef<number | null>(null);
  const returnHasMoved = useRef(false);
  const [searching, setSearching] = useState(false);
  const measure = useCallback(() => {
    const viewport = window.visualViewport;
    if (!viewport || viewport.scale !== 1) return 0;
    return Math.max(0, baseline.current.height - viewport.height);
  }, []);

  useLayoutEffect(() => {
    if (!enabled) return;
    const update = () => {
      if (window.visualViewport && window.visualViewport.scale !== 1) return;
      const width = window.innerWidth;
      const height = Math.max(
        window.innerHeight,
        window.visualViewport?.height ?? 0
      );
      if (width !== baseline.current.width) {
        searchKeyboardVisible.current = false;
        baseline.current = { width, height };
        setPreferredHeight(
          fallbackViewportRatio === undefined
            ? fallbackHeight
            : Math.min(
                fallbackHeight,
                Math.floor(height * fallbackViewportRatio)
              )
        );
        setReturning(false);
        setOpening(false);
      } else if (isEditable(document.activeElement) || open || returning) {
        baseline.current.height = Math.max(baseline.current.height, height);
      } else {
        baseline.current.height = height;
      }
      const inset = measure();
      setBaselineHeight(baseline.current.height);
      if (
        returnStartInset.current !== null &&
        Math.abs(inset - returnStartInset.current) > 1
      ) {
        returnHasMoved.current = true;
      }
      setKeyboardInset(inset);
    };
    update();
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', update);
    window.addEventListener('resize', update);
    return () => {
      viewport?.removeEventListener('resize', update);
      window.removeEventListener('resize', update);
    };
  }, [
    enabled,
    fallbackHeight,
    fallbackViewportRatio,
    measure,
    open,
    returning,
  ]);

  useEffect(() => {
    if (!enabled) return;
    const focus = () => {
      const inside = Boolean(
        panelRef.current?.contains(document.activeElement)
      );
      // Keep search presentation through its grid and toolbar controls. Only an
      // explicit return or focus outside the panel ends this interaction.
      if (!inside || isEditable(document.activeElement)) {
        setSearching(inside);
      }
    };
    const activateOutside = () => {
      if (!isEditable(document.activeElement)) setReturning(false);
    };
    document.addEventListener('focusin', focus);
    document.addEventListener('click', activateOutside, true);
    return () => {
      document.removeEventListener('focusin', focus);
      document.removeEventListener('click', activateOutside, true);
    };
  }, [enabled, panelRef]);

  useEffect(() => {
    if (!opening && !returning) return;
    // A hardware keyboard, rejected focus or interrupted gesture must not leave
    // a blank reservation behind. The normal path ends on viewport geometry.
    const timeout = window.setTimeout(() => {
      setOpening(false);
      setReturning(false);
    }, 900);
    return () => window.clearTimeout(timeout);
  }, [opening, returning]);

  useLayoutEffect(() => {
    // A fast reversal can still report the keyboard's old fully-open height.
    // Do not mistake that stale frame, or a crossing during animation, for completion.
    let settled: number | undefined;
    if (
      enabled &&
      returning &&
      returnHasMoved.current &&
      Math.abs(keyboardInset - preferredHeight) <= 1
    ) {
      settled = window.setTimeout(() => setReturning(false), 500);
    }
    if (keyboardInset <= 1 || !enabled) setOpening(false);
    if (!enabled) setReturning(false);
    return () => window.clearTimeout(settled);
  }, [enabled, returning, keyboardInset, preferredHeight]);

  const replacing = enabled && ((open && opening) || returning) && !searching;
  useLayoutEffect(() => {
    requestHeight?.(
      owner,
      replacing ? Math.max(0, baselineHeight - preferredHeight) : undefined
    );
  }, [requestHeight, owner, replacing, baselineHeight, preferredHeight]);
  useLayoutEffect(
    () => () => requestHeight?.(owner, undefined),
    [requestHeight, owner]
  );

  const prepareOpen = useCallback(() => {
    const inset = measure();
    // Browser chrome can alter the viewport by a few pixels without a keyboard.
    if (inset > 80 && !returning) setPreferredHeight(inset);
    setKeyboardInset(inset);
    returnStartInset.current = null;
    returnHasMoved.current = false;
    setSearching(false);
    setOpening(inset > 80);
    setReturning(false);
  }, [measure, returning]);
  const prepareReturn = useCallback(() => {
    if (!enabled) return;
    returnStartInset.current = measure();
    returnHasMoved.current = false;
    setSearching(false);
    setOpening(false);
    setReturning(true);
  }, [enabled, measure]);

  const exitSearch = useCallback(() => {
    prepareOpen();
    onSearchDismiss?.();
  }, [prepareOpen, onSearchDismiss]);

  useLayoutEffect(() => {
    if (!enabled || !open || !searching) {
      searchKeyboardVisible.current = false;
      return;
    }
    // Do not mistake the editor's outgoing keyboard during a quick search tap
    // for a dismissal of the search keyboard that has not opened yet.
    if (!opening && keyboardInset > 80) searchKeyboardVisible.current = true;
    if (searchKeyboardVisible.current && keyboardInset <= 1) {
      searchKeyboardVisible.current = false;
      exitSearch();
    }
  }, [enabled, open, searching, opening, keyboardInset, exitSearch]);

  return {
    searching: enabled && open && searching,
    preferredHeight,
    occludedHeight: searching
      ? 0
      : replacing && requestHeight
        ? Math.min(keyboardInset, preferredHeight)
        : keyboardInset,
    retained: enabled && returning,
    present: enabled && (open || returning),
    prepareOpen,
    prepareReturn,
    exitSearch,
  };
};
